import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle2, AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import ParticleBackground from "@/components/ParticleBackground";
const logo = "/assets/Touchworks-b.svg";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [step, setStep] = useState("form"); // form | sent | not-verified
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [notVerifiedData, setNotVerifiedData] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === "EMAIL_NOT_VERIFIED") {
          setNotVerifiedData(data);
          setStep("not-verified");
          return;
        }
        setError(data.error || "Erro ao processar solicitação.");
        return;
      }

      setStep("sent");
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendVerification() {
    if (isResending) return;
    setIsResending(true);
    setResendMessage("");

    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notVerifiedData?.email || email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage("Email de verificação reenviado! Verifique sua caixa de entrada.");
      } else {
        setResendMessage(data.error || "Erro ao reenviar. Tente novamente.");
      }
    } catch {
      setResendMessage("Erro de conexão. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 md:p-10 bg-background text-foreground font-['Space_Grotesk'] relative overflow-hidden">
      <ParticleBackground />

      <div className="relative z-20 w-full max-w-sm md:max-w-lg">
        <Card className="overflow-hidden border border-white/5 bg-surface/95 backdrop-blur-sm shadow-2xl">
          <CardContent className="p-6 md:p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img src={logo} alt="Touchworks Logo" className="h-7 w-auto" />
            </div>

            {/* ── Step: Form ── */}
            {step === "form" && (
              <div className="flex flex-col gap-6">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/10 border border-cyan-500/20">
                    <Mail className="h-8 w-8 text-cyan-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground-strong">Esqueceu sua senha?</h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    Digite seu email e enviaremos um link para redefinir sua senha.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="email" className="text-foreground-strong">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-background border-border text-foreground-strong placeholder:text-placeholder focus-visible:ring-cyan-500 h-10"
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/20 rounded-lg p-3">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Enviar link de recuperação"
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-cyan-500 hover:text-cyan-400 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar ao login
                  </Link>
                </div>
              </div>
            )}

            {/* ── Step: Email Sent ── */}
            {step === "sent" && (
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground-strong mb-2">
                    Verifique seu email
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Se o email <span className="text-foreground-strong font-medium">{email}</span> estiver
                    cadastrado, você receberá um link de recuperação em alguns instantes.
                  </p>
                </div>

                <div className="w-full bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground">
                    💡 <strong className="text-foreground-strong">Dica:</strong> Verifique também sua caixa de spam.
                    O link expira em <strong className="text-foreground-strong">30 minutos</strong>.
                  </p>
                </div>

                <div className="flex flex-col gap-2 w-full">
                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10"
                  >
                    Voltar ao login
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setStep("form");
                      setEmail("");
                    }}
                    className="w-full text-muted-foreground hover:text-foreground-strong"
                  >
                    Tentar outro email
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: Not Verified ── */}
            {step === "not-verified" && (
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="h-8 w-8 text-amber-700 dark:text-amber-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground-strong mb-2">
                    Conta não verificada
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Sua conta ainda não foi verificada. Antes de recuperar a senha, você
                    precisa verificar seu email.
                  </p>
                </div>

                <div className="w-full bg-amber-500/5 border border-amber-500/10 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground">
                    📩 Enviamos um email de verificação quando você se cadastrou.
                    Verifique sua caixa de entrada ou clique abaixo para reenviar.
                  </p>
                </div>

                {resendMessage && (
                  <div className={`text-sm px-4 py-2 rounded-lg w-full ${
                    resendMessage.includes("reenviado")
                      ? "bg-emerald-500/15 dark:bg-emerald-500/10 border border-emerald-500/30 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400"
                      : "bg-red-500/15 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/20 text-red-700 dark:text-red-400"
                  }`}>
                    {resendMessage}
                  </div>
                )}

                <div className="flex flex-col gap-2 w-full">
                  <Button
                    onClick={handleResendVerification}
                    disabled={isResending}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold h-10"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Reenviando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reenviar email de verificação
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/login")}
                    className="w-full text-muted-foreground hover:text-foreground-strong"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar ao login
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground mt-6">
          Precisa de ajuda?{" "}
          <a
            href="https://wa.me/551152867786"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground-strong"
          >
            Fale conosco
          </a>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 z-50"></div>
    </div>
  );
}
